/**
 * Frame383 - Settlement Welcome Screen
 *
 * Purpose: First screen executors see when entering settlement flow.
 * Provides orientation, context, and clear next steps.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, Shield, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { BrandSymbol } from '../ui/BrandSymbol';

export default function Frame383() {
  const navigate = useNavigate();
  const { estateId } = useParams();

  const handleGetStarted = () => {
    if (estateId) {
      navigate(`/sframe-111/${estateId}`);
    } else {
      navigate('/sframe-111');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <BrandSymbol size="3xl" showTagline={false} />
          <div className="text-sm text-gray-600">Settlement Guide</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Welcome to Estate Settlement
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              You've been designated as an executor. This guide will walk you through each step
              of settling the estate with clarity and confidence.
            </p>
          </div>

          {/* Key Features */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-cyan-100 rounded-full p-3">
                  <Shield className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Step-by-Step Guidance
                  </h3>
                  <p className="text-gray-600">
                    Clear instructions for each phase, from securing assets to final distribution.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 rounded-full p-3">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Progress Tracking</h3>
                  <p className="text-gray-600">
                    Track completion status for every task and see your overall progress.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-full p-3">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Collaborative Workflow
                  </h3>
                  <p className="text-gray-600">
                    Multiple executors can work together, with real-time updates and shared data.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 rounded-full p-3">
                  <Heart className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Estate Plan Access
                  </h3>
                  <p className="text-gray-600">
                    View the deceased's estate planning information to guide your decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What to Expect */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What to Expect</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Immediately After Death</h4>
                  <p className="text-gray-600">
                    Secure the residence, notify authorities, obtain death certificates, and handle
                    funeral arrangements.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Secure & Inventory Assets
                  </h4>
                  <p className="text-gray-600">
                    Locate all accounts, property, and valuables. Secure important documents and
                    begin asset tracking.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Probate & Legal Process</h4>
                  <p className="text-gray-600">
                    File necessary court petitions, obtain legal authority, and establish estate
                    accounts.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-semibold">
                  4
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Notify & Settle</h4>
                  <p className="text-gray-600">
                    Notify institutions and creditors, settle debts and taxes, and manage estate
                    expenses.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-semibold">
                  5
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Distribute & Close</h4>
                  <p className="text-gray-600">
                    Distribute assets to beneficiaries, obtain receipts, file final reports, and
                    close the estate.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={handleGetStarted}
              size="xl"
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-12 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-3"
            >
              Get Started
              <ArrowRight className="w-6 h-6" />
            </Button>
            <p className="text-gray-500 text-sm mt-4">
              This should take approximately 3-6 months, depending on estate complexity
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto text-center text-sm text-gray-600">
          Need help? Contact support or consult with a legal professional for guidance.
        </div>
      </footer>
    </div>
  );
}
